import { useEffect, useMemo, useState } from "react";
import { poolIdentifier } from "../api/types";
import type { EvmQuoteMode, NativeBridgeCandidate, PoolCandidate, SendMode, Task, TaskAction, TaskPoolRef, Token } from "../api/types";
import { TokenPicker } from "./TokenPicker";
import { WalletsInput } from "./WalletsInput";
import { FieldHelp } from "./FieldHelp";
import { ChevronDown, Trash2, Upload, X } from "lucide-react";
import { api, ApiError } from "../api/client";

const NATIVE_ETH = "0x0000000000000000000000000000000000000000";
const ADVANCED_LABEL_CLASS = "label mb-0 flex min-h-10 flex-wrap items-center gap-x-1.5 gap-y-1";
const CHAIN_WETH = {
  robinhood: "0x0bd7d308f8e1639fab988df18a8011f41eacad73",
  base: "0x4200000000000000000000000000000000000006",
} as const;

function bridgeTvlLabel(pool: NativeBridgeCandidate): string {
  if (pool.liquidityUsd !== null) {
    return `$${pool.liquidityUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  if (pool.liquidityNativeWei) {
    try {
      const native = Number(BigInt(pool.liquidityNativeWei)) / 1e18;
      if (Number.isFinite(native) && native > 0) {
        return `≈ ${native.toLocaleString(undefined, { maximumFractionDigits: 4 })} ETH on-chain`;
      }
    } catch {
      // Keep the explicit fallback below for malformed/legacy API responses.
    }
  }
  return "not available";
}

type Props = {
  mode: "create" | "edit";
  initial?: Task;
  /** When opening "+ add task" from a token-group header, lock TokenPicker to this id. */
  initialTokenId?: string;
  /** All available tokens (load via useTokens). */
  tokens: Token[];
  /** Inline token creation from within TaskEditor (passes through to TokenPicker). */
  onCreateToken: (body: unknown) => Promise<Token>;
  onCancel: () => void;
  onSubmit: (body: unknown) => Promise<void>;
};

export function TaskEditor({
  mode,
  initial,
  initialTokenId,
  tokens,
  onCreateToken,
  onCancel,
  onSubmit,
}: Props): JSX.Element {
  const [tokenId, setTokenId] = useState<string>(
    initial?.tokenId ?? initialTokenId ?? "",
  );
  const [name, setName] = useState(initial?.name ?? "");
  const [action, setAction] = useState<TaskAction>(initial?.action ?? "buy");
  const [walletKeys, setWalletKeys] = useState("");
  const [walletPubkeysToKeep, setWalletPubkeysToKeep] = useState<string[]>(
    initial?.walletPubkeys ?? [],
  );

  const [minTriggerUsd, setMinTriggerUsd] = useState<string>(
    initial?.config.minTriggerUsd?.toString() ?? "",
  );
  const [maxTriggerUsd, setMaxTriggerUsd] = useState<string>(
    initial?.config.maxTriggerUsd?.toString() ?? "",
  );
  const [mcMin, setMcMin] = useState<string>(
    initial?.config.mcRangeUsd?.[0].toString() ?? "",
  );
  const [mcMax, setMcMax] = useState<string>(
    initial?.config.mcRangeUsd?.[1].toString() ?? "",
  );
  const [pctMin, setPctMin] = useState<string>(
    initial?.config.counterSizePctRange?.[0].toString() ?? "10",
  );
  const [pctMax, setPctMax] = useState<string>(
    initial?.config.counterSizePctRange?.[1].toString() ?? "20",
  );
  const [maxPositionSize, setMaxPositionSize] = useState<string>(
    (initial?.config.maxPositionSol ?? initial?.config.maxPositionEth)?.toString() ?? "",
  );
  const [maxTotalExposure, setMaxTotalExposure] = useState<string>(
    (initial?.config.maxTotalExposureSol ?? initial?.config.maxTotalExposureEth)?.toString() ??
      "",
  );
  const [sendMode, setSendMode] = useState<SendMode>(
    initial?.config.sendMode ?? "auto",
  );
  const [solanaSettlement, setSolanaSettlement] = useState<"pool-quote" | "sol">(
    initial?.config.solanaSettlement ?? "pool-quote",
  );
  // EVM-only: execution venue picker. General modes are stored as quoteMode;
  // exact pool selection is stored as executionPool and overrides quoteMode.
  const initialQuote = initial?.config.quoteMode;
  const initialChain =
    initial?.token?.chain ?? tokens.find((token) => token.id === initialTokenId)?.chain;
  const normalizedInitialQuote: Exclude<EvmQuoteMode, "native" | "pool"> =
    initialQuote === "native"
      ? "weth"
      : initialQuote === "pool"
        ? "trigger"
        : (initialQuote ?? (initialChain === "arc" ? "usdc" : "weth"));
  const [executionRoute, setExecutionRoute] = useState<string>(
    initial?.config.executionPool
      ? `pool:${initial.config.executionPool.toLowerCase()}`
      : `mode:${normalizedInitialQuote}`,
  );
  const [settleNative, setSettleNative] = useState(
    initial?.config.settleNative ?? false,
  );
  const [nativeBridgePool, setNativeBridgePool] = useState(
    initial?.config.nativeBridgePool ?? "",
  );
  const [bridgeCandidates, setBridgeCandidates] = useState<NativeBridgeCandidate[]>([]);
  const [bridgeQuote, setBridgeQuote] = useState<string | null>(null);
  const [bridgeLoading, setBridgeLoading] = useState(false);
  const [bridgeError, setBridgeError] = useState<string | null>(null);
  const [knownSlippagePct, setKnownSlippagePct] = useState(
    ((initial?.config.slippageBps.known ?? 100) / 100).toString(),
  );
  const [volatileSlippagePct, setVolatileSlippagePct] = useState(
    ((initial?.config.slippageBps.volatile ?? 500) / 100).toString(),
  );
  const [aggregation, setAggregation] = useState<"" | "per-tx" | "per-block">(
    initial?.config.aggregation ?? "",
  );
  const [minLegUsd, setMinLegUsd] = useState(initial?.config.minLegUsd?.toString() ?? "");
  const [maxGasPct, setMaxGasPct] = useState(initial?.config.maxGasPctOfTrade?.toString() ?? "");
  const [traderCapUsd, setTraderCapUsd] = useState(
    initial?.config.maxCounterUsdPerTraderPerHour?.toString() ?? "",
  );
  const [priceLimitUsd, setPriceLimitUsd] = useState(initial?.config.priceLimitUsd?.toString() ?? "");
  const [preApprove, setPreApprove] = useState(initial?.config.preApprove ?? true);
  const [backrun, setBackrun] = useState(initial?.config.mempoolExecution === "bundle");
  const [ignoreWalletsText, setIgnoreWalletsText] = useState<string>(
    initial?.config.ignoreWallets?.join("\n") ?? "",
  );
  const [ignoreImporting, setIgnoreImporting] = useState(false);
  const [ignoreImportResult, setIgnoreImportResult] = useState<string | null>(null);
  const [ignoreImportPassword, setIgnoreImportPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derive chain + mint from the selected token. In edit mode the token is
  // locked (immutable); in create mode the user picks via TokenPicker.
  const selectedToken = useMemo(
    () => tokens.find((t) => t.id === tokenId) ?? initial?.token ?? null,
    [tokens, tokenId, initial?.token],
  );
  const chain = selectedToken?.chain ?? "solana";
  const mint = selectedToken?.mint ?? "";
  // EVM chains share the same strategy surface; BSC displays BNB as native unit.
  // strategy fields, EVM send modes). `isEvm` gates all EVM-vs-Solana copy.
  const isEvm = chain === "base" || chain === "ethereum" || chain === "robinhood" || chain === "bnb" || chain === "ink" || chain === "arc";
  const hasNativeSettlement = chain === "robinhood" || chain === "base";
  const nativeUnit = chain === "bnb" ? "BNB" : chain === "arc" ? "USDC" : isEvm ? "ETH" : "SOL";

  function shortId(id: string): string {
    return id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
  }

  function poolLabel(pool: TaskPoolRef, index: number): string {
    const id = poolIdentifier(pool);
    return `${pool.dex} ${shortId(id)}${index === 0 ? " (primary)" : ""}`;
  }

  const executionPoolOptions = useMemo(
    () =>
      isEvm && selectedToken
        ? selectedToken.pools.map((p, i) => ({
            id: poolIdentifier(p).toLowerCase(),
            label: poolLabel(p, i),
          }))
        : [],
    [isEvm, selectedToken],
  );

  useEffect(() => {
    if (!isEvm || !executionRoute.startsWith("pool:")) return;
    const selectedPool = executionRoute.slice("pool:".length).toLowerCase();
    if (!executionPoolOptions.some((p) => p.id === selectedPool)) {
      setExecutionRoute("mode:trigger");
    }
  }, [executionPoolOptions, executionRoute, isEvm]);

  useEffect(() => {
    if (chain === "arc" && executionRoute === "mode:weth") {
      setExecutionRoute("mode:usdc");
    }
  }, [chain, executionRoute]);

  useEffect(() => {
    if (!hasNativeSettlement || !settleNative || !selectedToken || selectedToken.pools.length === 0) {
      setBridgeCandidates([]);
      setBridgeQuote(null);
      setBridgeError(null);
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      setBridgeLoading(true);
      setBridgeError(null);
      try {
        const selectedId = executionRoute.startsWith("pool:")
          ? executionRoute.slice("pool:".length).toLowerCase()
          : poolIdentifier(selectedToken.pools[0]!).toLowerCase();
        const stored = selectedToken.pools.find((pool) => poolIdentifier(pool).toLowerCase() === selectedId);
        const otherCurrency = stored?.currency0?.toLowerCase() === selectedToken.mint.toLowerCase()
          ? stored.currency1
          : stored?.currency1?.toLowerCase() === selectedToken.mint.toLowerCase()
            ? stored.currency0 : undefined;
        let quote = stored?.quoteMint ?? otherCurrency;
        if (!quote) {
          const discovered = await api.post<{ pools: PoolCandidate[] }>("/api/pools/discover", {
            chain,
            mint: selectedToken.mint,
          });
          const match = discovered.pools.find((pool) => {
            const id = (pool.poolId ?? pool.poolAccount).toLowerCase();
            return id === selectedId;
          }) ?? discovered.pools[0];
          quote = match?.quoteMint;
        }
        if (!quote || quote.toLowerCase() === selectedToken.mint.toLowerCase()) {
          throw new Error("Could not resolve the selected pool quote token");
        }
        const weth = CHAIN_WETH[chain as keyof typeof CHAIN_WETH];
        if (quote.toLowerCase() === weth || quote.toLowerCase() === NATIVE_ETH) {
          if (!cancelled) {
            setBridgeQuote(null);
            setBridgeCandidates([]);
          }
          return;
        }
        const result = await api.post<{ pools: NativeBridgeCandidate[] }>("/api/pools/native-bridges", {
          chain,
          quoteMint: quote,
        });
        if (!cancelled) {
          setBridgeQuote(quote.toLowerCase());
          setBridgeCandidates(result.pools);
          if (result.pools.length === 0) setBridgeError("No verified liquid WETH/quote bridge was found");
        }
      } catch (err) {
        if (!cancelled) setBridgeError(err instanceof Error ? err.message : "Bridge discovery failed");
      } finally {
        if (!cancelled) setBridgeLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [chain, executionRoute, selectedToken, settleNative, hasNativeSettlement]);

  const quoteMode: EvmQuoteMode = executionRoute.startsWith("mode:")
    ? (executionRoute.slice("mode:".length) as EvmQuoteMode)
    : "trigger";

  function parseLines(text: string): string[] {
    return text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function uniqueLines(lines: string[]): string[] {
    return Array.from(new Set(lines));
  }

  const ignoreCount = uniqueLines(parseLines(ignoreWalletsText)).length;
  const newWalletKeys = parseLines(walletKeys);
  const walletListChanged =
    mode === "edit" &&
    initial !== undefined &&
    (walletPubkeysToKeep.length !== initial.walletPubkeys.length ||
      walletPubkeysToKeep.some((pk, i) => pk !== initial.walletPubkeys[i]));

  async function importIgnoreWallets(file: File): Promise<void> {
    setIgnoreImporting(true);
    setIgnoreImportResult(null);
    setError(null);
    try {
      const form = new FormData();
      form.append("chain", chain);
      if (ignoreImportPassword) form.append("password", ignoreImportPassword);
      form.append("file", file, file.name);
      const result = await api.postForm<{
        wallets: string[];
        invalidRows: Array<{ index: number; error: string }>;
        convertedPrivateKeys: number;
        cellsScanned: number;
        sheetCount: number;
        encrypted: boolean;
      }>("/api/tasks/ignore-wallets/import", form);
      const merged = uniqueLines([
        ...parseLines(ignoreWalletsText),
        ...result.wallets,
      ]);
      setIgnoreWalletsText(merged.join("\n"));
      setIgnoreImportPassword("");
      setIgnoreImportResult(
        `Imported ${result.wallets.length} wallet${result.wallets.length === 1 ? "" : "s"}` +
          ` from ${result.sheetCount} sheet${result.sheetCount === 1 ? "" : "s"}` +
          (result.encrypted ? " · password-protected file decrypted" : "") +
          (result.convertedPrivateKeys > 0
            ? ` · converted ${result.convertedPrivateKeys} private key${result.convertedPrivateKeys === 1 ? "" : "s"} to public addresses`
            : "") +
          (result.invalidRows.length > 0
            ? ` · skipped ${result.invalidRows.length} unrecognized cell${result.invalidRows.length === 1 ? "" : "s"}`
            : ""),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Excel import failed");
    } finally {
      setIgnoreImporting(false);
    }
  }

  async function submit(): Promise<void> {
    setError(null);

    if (mode === "create") {
      if (!name.trim()) return setError("Name required");
      if (!tokenId) return setError("Pick or create a token first");
    } else {
      if (!name.trim()) return setError("Name required");
    }

    const ignoreList = uniqueLines(parseLines(ignoreWalletsText));
    const minTrigger = minTriggerUsd ? Number(minTriggerUsd) : undefined;
    const maxTrigger = maxTriggerUsd ? Number(maxTriggerUsd) : undefined;
    if (minTrigger !== undefined && (!Number.isFinite(minTrigger) || minTrigger < 0)) {
      return setError("Min trigger must be a non-negative USD amount");
    }
    if (maxTrigger !== undefined && (!Number.isFinite(maxTrigger) || maxTrigger < 0)) {
      return setError("Max trigger must be a non-negative USD amount");
    }
    if (minTrigger !== undefined && maxTrigger !== undefined && maxTrigger < minTrigger) {
      return setError("Max trigger must be greater than or equal to min trigger");
    }

    // EVM send modes are a subset — coerce legacy picks.
    const resolvedSendMode: SendMode = isEvm
      ? sendMode === "rpc" || sendMode === "auto"
        ? sendMode
        : "auto"
      : sendMode;

    const slippage = isEvm
      ? {
          known: Math.round(Number(knownSlippagePct) * 100),
          volatile: Math.round(Number(volatileSlippagePct) * 100),
        }
      : { known: 500, volatile: 1500 };
    if (isEvm && (!Number.isFinite(slippage.known) || !Number.isFinite(slippage.volatile) || slippage.known < 0 || slippage.volatile < slippage.known || slippage.volatile > 10_000)) {
      return setError("Slippage must be valid percentages; retry must be at least initial and at most 100%");
    }

    const strategy: Record<string, unknown> = {
      action,
      minTriggerUsd: minTrigger,
      maxTriggerUsd: maxTrigger,
      // Marketcap window applies to both Solana and EVM. EVM derives FDV from
      // ERC20 `totalSupply()` × live tokenUsd at task start.
      mcRangeUsd: mcMin && mcMax ? [Number(mcMin), Number(mcMax)] : undefined,
      counterSizePctRange: [Number(pctMin), Number(pctMax)] as [number, number],
      slippageBps: slippage,
      emergencyStopFile: "./HALT",
      ignoreWallets: ignoreList.length > 0 ? ignoreList : undefined,
      sendMode: resolvedSendMode,
    };
    if (isEvm) {
      const positive = (value: string, label: string): number | undefined | null => {
        if (!value.trim()) return undefined;
        const n = Number(value);
        if (!Number.isFinite(n) || n <= 0) {
          setError(`${label} must be a positive number`);
          return null;
        }
        return n;
      };
      const minLeg = positive(minLegUsd, "Min leg USD");
      const gasPct = positive(maxGasPct, "Max gas %");
      const traderCap = positive(traderCapUsd, "Per-trader cap");
      const priceLimit = positive(priceLimitUsd, "Price limit");
      if (minLeg === null || gasPct === null || traderCap === null || priceLimit === null) return;
      if (gasPct !== undefined && gasPct > 100) return setError("Max gas % must be at most 100");
      strategy.aggregation = aggregation || undefined;
      strategy.minLegUsd = minLeg;
      strategy.maxGasPctOfTrade = gasPct;
      strategy.maxCounterUsdPerTraderPerHour = traderCap;
      strategy.priceLimitUsd = priceLimit;
      // Only Ethereum SELL tasks pre-approve; omit the default.
      strategy.preApprove = chain === "ethereum" && action === "sell" && !preApprove ? false : undefined;
      strategy.mempoolExecution = chain === "ethereum" && backrun ? "bundle" : undefined;
      strategy.maxPositionEth = maxPositionSize ? Number(maxPositionSize) : undefined;
      strategy.maxTotalExposureEth = maxTotalExposure ? Number(maxTotalExposure) : undefined;
      if (executionRoute.startsWith("pool:")) {
        strategy.quoteMode = "trigger";
        strategy.executionPool = executionRoute.slice("pool:".length);
      } else {
        strategy.quoteMode = executionRoute.slice("mode:".length) as EvmQuoteMode;
      }
      // Persist both choices. Omitting `false` leaves saved task configs
      // ambiguous to older clients and made it too easy to retain ETH
      // settlement after the editor had been switched back to the pool quote.
      if (hasNativeSettlement) {
        strategy.settleNative = settleNative;
        if (settleNative && nativeBridgePool.trim()) {
          if (!/^0x[a-fA-F0-9]{40}$/.test(nativeBridgePool.trim())) {
            return setError("Bridge pool must be a 0x-prefixed 40-hex address");
          }
          strategy.nativeBridgePool = nativeBridgePool.trim().toLowerCase();
        }
      }
    } else {
      strategy.maxPositionSol = maxPositionSize ? Number(maxPositionSize) : undefined;
      strategy.maxTotalExposureSol = maxTotalExposure ? Number(maxTotalExposure) : undefined;
      strategy.solanaSettlement = solanaSettlement;
    }

    const body: Record<string, unknown> = {
      name: name.trim(),
      action,
      strategy,
      dryRun: false,
    };
    // Only set tokenId on create — it's immutable on edit (server enforces).
    if (mode === "create") {
      body.tokenId = tokenId;
    }

    if (mode === "create") {
      body.walletKeys = newWalletKeys;
      if ((body.walletKeys as string[]).length === 0) {
        setError("Add at least one wallet private key");
        return;
      }
    } else if (walletListChanged || newWalletKeys.length > 0) {
      body.keepWalletPubkeys = walletPubkeysToKeep;
      if (newWalletKeys.length > 0) {
        body.walletKeysToAdd = newWalletKeys;
      }
    }

    setSubmitting(true);
    try {
      await onSubmit(body);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop fixed inset-0 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-12">
      <div className="w-full max-w-3xl bg-bg border border-fg mx-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="font-serif text-xl">
            {mode === "create" ? "Add task" : "Edit task"}
          </h3>
          <button onClick={onCancel} className="text-muted hover:text-fg">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="label flex items-center gap-1.5">
              Token
              <FieldHelp>Select the token whose trades this task watches. Its saved pools are used for detection and execution.</FieldHelp>
            </label>
            <TokenPicker
              chain={chain}
              value={tokenId}
              onChange={setTokenId}
              tokens={tokens}
              onCreate={onCreateToken}
              disabled={mode === "edit" || Boolean(initialTokenId)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="label flex items-center gap-1.5">
                Name
                <FieldHelp>A private label for recognising this task in the token list. It does not affect trading.</FieldHelp>
              </label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  selectedToken
                    ? `${action === "buy" ? "Buy" : "Sell"} ${selectedToken.name}`
                    : "Strategy name"
                }
              />
            </div>
            <div>
              <label className="label flex items-center gap-1.5">
                Action
                <FieldHelp>BUY reacts to external sales by buying the token. SELL reacts to external buys by selling tokens from your wallets.</FieldHelp>
              </label>
              <div className="flex gap-4 py-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={action === "buy"}
                    onChange={() => setAction("buy")}
                  />
                  <span>
                    BUY <span className="text-muted">(react to sells)</span>
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={action === "sell"}
                    onChange={() => setAction("sell")}
                  />
                  <span>
                    SELL <span className="text-muted">(react to buys)</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="divider" />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label flex items-center gap-1.5">
                Min trigger USD
                <FieldHelp>Ignore a detected buy or sell when its estimated size is below this amount. Leave empty to react to every eligible size.</FieldHelp>
                <span className="text-muted normal-case tracking-normal">(optional)</span>
              </label>
              <input
                className="input"
                type="number"
                min="0"
                value={minTriggerUsd}
                onChange={(e) => setMinTriggerUsd(e.target.value)}
                placeholder="e.g. 4000 — cut off smaller trades"
              />
            </div>
            <div>
              <label className="label flex items-center gap-1.5">
                Max trigger USD
                <FieldHelp>Ignore a detected buy or sell when its estimated size is above this amount. Leave empty for no upper limit.</FieldHelp>
                <span className="text-muted normal-case tracking-normal">(optional)</span>
              </label>
              <input
                className="input"
                type="number"
                min="0"
                value={maxTriggerUsd}
                onChange={(e) => setMaxTriggerUsd(e.target.value)}
                placeholder="e.g. 25000 — ignore larger trades"
              />
            </div>
            <div>
              <label className="label flex items-center gap-1.5">
                Marketcap range USD
                <FieldHelp>The task trades only while live market cap or FDV is within both bounds. Leave both fields empty to disable this filter.</FieldHelp>
                <span className="text-muted normal-case tracking-normal">(optional)</span>
              </label>
              <div className="flex gap-2">
                <input
                  className="input"
                  type="number"
                  value={mcMin}
                  onChange={(e) => setMcMin(e.target.value)}
                  placeholder="min"
                />
                <input
                  className="input"
                  type="number"
                  value={mcMax}
                  onChange={(e) => setMcMax(e.target.value)}
                  placeholder="max"
                />
              </div>
              <div className="text-[11px] text-muted mt-1">
                {isEvm ? "FDV = totalSupply × tokenUsd" : "live marketcap"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label flex items-center gap-1.5">
                Counter-trade %
                <FieldHelp>Random share of each eligible trigger. For a $100 sale and 24-26%, the bot buys about $24-26 of the token.</FieldHelp>
              </label>
              <div className="flex gap-2 items-center">
                <input
                  className="input"
                  type="number"
                  value={pctMin}
                  onChange={(e) => setPctMin(e.target.value)}
                />
                <span className="text-muted">–</span>
                <input
                  className="input"
                  type="number"
                  value={pctMax}
                  onChange={(e) => setPctMax(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="label flex items-center gap-1.5">
                Max position, {nativeUnit}
                <FieldHelp>Maximum size of one wallet leg. A larger response can be split across several eligible wallets; this is not a limit for the whole task.</FieldHelp>
                <span className="text-muted normal-case tracking-normal">(optional)</span>
              </label>
              <input
                className="input"
                type="number"
                step="0.01"
                value={maxPositionSize}
                onChange={(e) => setMaxPositionSize(e.target.value)}
                placeholder="empty = no cap"
              />
            </div>
            <div>
              <label className="label flex items-center gap-1.5">
                Max exposure, {nativeUnit}
                <FieldHelp>Maximum cumulative amount this BUY task may spend while it is running. When reached, new eligible sales are skipped with exposure-limit.</FieldHelp>
                <span className="text-muted normal-case tracking-normal">(optional)</span>
              </label>
              <input
                className="input"
                type="number"
                step="0.1"
                value={maxTotalExposure}
                onChange={(e) => setMaxTotalExposure(e.target.value)}
                placeholder="empty = no cap"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="label flex items-center gap-1.5">
                Send mode
                <FieldHelp>
                  {isEvm
                    ? "Auto uses the supported direct pool route and its configured fallback. RPC only sends through the chain RPC and does not use an aggregator fallback."
                    : "Auto tries Jito, then RPC, then a fresh Jupiter route. The other modes restrict sending to that one path."}
                </FieldHelp>
              </label>
              <select
                className="input"
                value={sendMode}
                onChange={(e) => setSendMode(e.target.value as SendMode)}
              >
                {isEvm ? (
                  <>
                    <option value="auto">
                      {chain === "robinhood" || chain === "bnb" || chain === "ink" || chain === "arc" ? "Auto: Direct pools only" : "Auto: Direct → 0x fallback"}
                    </option>
                    <option value="rpc">RPC only (skip aggregator)</option>
                  </>
                ) : (
                  <>
                    <option value="auto">Cascade: Jito → RPC → Jupiter</option>
                    <option value="jito">Jito only</option>
                    <option value="rpc">RPC only</option>
                    <option value="jupiter">Jupiter relay</option>
                  </>
                )}
              </select>
            </div>
            {isEvm && (
              <div>
                <label className="label flex items-center gap-1.5">
                  Execute through
                  <FieldHelp>Choose the settlement quote for the counter-trade. This determines what your wallets spend on BUY and receive on SELL.</FieldHelp>
                  <span className="text-muted normal-case tracking-normal ml-2">
                    counter-trade routing
                  </span>
                </label>
                <select
                  className="input"
                  value={executionRoute}
                  onChange={(e) => setExecutionRoute(e.target.value)}
                >
                  <option value="mode:trigger">Same as trigger pool</option>
                  {chain !== "arc" && <option value="mode:weth">ETH / WETH</option>}
                  <option value="mode:usdc">
                    {chain === "arc" ? "USDC (Arc)" : "Stable quote"}
                  </option>
                  {executionPoolOptions.length > 0 && (
                    <optgroup label="Specific pool">
                      {executionPoolOptions.map((p) => (
                        <option key={p.id} value={`pool:${p.id}`}>
                          {p.label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {executionRoute.startsWith("pool:") ? (
                  <div className="text-[11px] text-muted mt-1">
                    All triggers route through the selected pool.
                  </div>
                ) : (
                  <div className="text-[11px] text-muted mt-1">
                    {chain === "arc"
                      ? "Arc counter-trades use the verified launchpad route. Lift uses ERC-20 USDC; Full.fun curves use native Arc USDC."
                      : quoteMode === "weth" || quoteMode === "native"
                        ? "Uses a direct WETH/native-quoted pool. Automatic V2 multi-hop routing is disabled; select a direct WETH pool or settle in the pool quote token."
                        : quoteMode === "usdc"
                          ? "All triggers route through a known stable-paired pool. Wallets must hold that stable for buys. If task has no stable pool, execution skips."
                          : "Execute in the same pool that fired the trigger — uses that pool's quote (WETH, stable, etc.)."}
                  </div>
                )}
              </div>
            )}
            {!isEvm && (
              <div>
                <label className="label flex items-center gap-1.5">
                  Buy with / sell to
                  <FieldHelp>Choose whether counter-trades settle in the trigger pool quote token or always in native SOL.</FieldHelp>
                  <span className="text-muted normal-case tracking-normal ml-2">
                    settlement asset
                  </span>
                </label>
                <select
                  className="input"
                  value={solanaSettlement}
                  onChange={(e) => setSolanaSettlement(e.target.value as "pool-quote" | "sol")}
                >
                  <option value="pool-quote">Pool quote token</option>
                  <option value="sol">Native SOL (atomic Jupiter V2)</option>
                </select>
                <div className="text-[11px] text-muted mt-1">
                  {solanaSettlement === "sol"
                    ? "BUY spends SOL and SELL returns SOL. Arbitrary quote-token hops, including both StonkFun launch modes, execute atomically in one Jupiter V2 transaction."
                    : "Uses the selected pool quote token and requires that balance for buys."}
                </div>
              </div>
            )}
          </div>

          {hasNativeSettlement && (
            <div className="mt-4 w-full max-w-xl">
              <label className="label flex items-center gap-1.5">
                Buy with / sell to
                <FieldHelp>Choose whether the wallet uses the pool quote token or native ETH through the selected quote/WETH pool.</FieldHelp>
                <span className="text-muted normal-case tracking-normal ml-2">
                  settlement asset
                </span>
              </label>
              <select
                className="input"
                value={settleNative ? "eth" : "pool"}
                onChange={(e) => setSettleNative(e.target.value === "eth")}
              >
                <option value="pool">Pool quote token</option>
                <option value="eth">Native ETH</option>
              </select>
              <div className="text-[11px] text-muted mt-1">
                {settleNative
                  ? chain === "base"
                    ? "BUY spends ETH and SELL returns ETH through the selected V4 token pool and a V2/V3 quote/WETH pool in one transaction."
                    : "Uses the selected target/quote pool plus a verified quote/WETH bridge. Compatible Robinhood V2/V3 routes execute atomically in one transaction; unsupported routes use recoverable two-stage execution."
                  : "Wallets use the selected pool quote token. This preserves the current routing behavior."}
              </div>
              {settleNative && bridgeQuote && (
                <div className="mt-3 space-y-2">
                  <label className="label flex items-center gap-1.5">
                    WETH / quote bridge pool
                    <FieldHelp>Leave empty for Auto. Auto verifies factory, pair assets and live liquidity, then selects the pool with the best quote for the actual trade amount. Paste an address to pin one exact V2/V3 pool.</FieldHelp>
                  </label>
                  <input
                    className="input font-mono text-xs"
                    value={nativeBridgePool}
                    onChange={(e) => setNativeBridgePool(e.target.value)}
                    list="native-bridge-candidates"
                    placeholder={bridgeCandidates[0] ? `Auto / recommended: ${bridgeCandidates[0].address}` : "Auto — best verified live quote"}
                  />
                  <datalist id="native-bridge-candidates">
                    {bridgeCandidates.map((pool) => <option key={pool.address} value={pool.address} />)}
                  </datalist>
                  <div className="text-[11px] text-muted break-all">Quote token: {bridgeQuote}</div>
                  {bridgeLoading && <div className="text-[11px] text-muted">Checking bridge fee, TVL and live quote…</div>}
                  {bridgeError && <div className="text-[11px] text-warn">{bridgeError}</div>}
                  {bridgeCandidates.map((pool) => (
                    <button
                      type="button"
                      key={pool.address}
                      onClick={() => setNativeBridgePool(pool.address)}
                      className="block w-full text-left border border-border px-3 py-2 text-[11px] hover:bg-hover"
                    >
                      <span className={pool.recommended ? "text-ok" : "text-fg"}>
                        {pool.recommended ? "Recommended · " : ""}{pool.dex} · fee {(pool.feeBps / 100).toFixed(2)}%
                      </span>
                      <span className="text-muted ml-2">
                        TVL {bridgeTvlLabel(pool)}
                      </span>
                      <div className="font-mono text-muted mt-1">{pool.address}</div>
                    </button>
                  ))}
                  <div className="text-[11px] text-muted">
                    Empty field = Auto. A pinned address is re-verified on-chain every time the task starts.
                  </div>
                </div>
              )}
            </div>
          )}

          {isEvm && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label flex items-center gap-1.5">
                  Initial slippage, %
                  <FieldHelp>Used for the first attempt. Tax tokens need this above their transfer tax plus price movement; Full.fun JOLLYBOT currently needs more than 2%.</FieldHelp>
                </label>
                <input className="input" type="number" min="0" max="100" step="0.1" value={knownSlippagePct} onChange={(e) => setKnownSlippagePct(e.target.value)} />
              </div>
              <div>
                <label className="label flex items-center gap-1.5">
                  Retry slippage, %
                  <FieldHelp>Used once only after a confirmed revert. It is never widened beyond this explicit task value.</FieldHelp>
                </label>
                <input className="input" type="number" min="0" max="100" step="0.1" value={volatileSlippagePct} onChange={(e) => setVolatileSlippagePct(e.target.value)} />
              </div>
            </div>
          )}

          {isEvm && (
            <details className="group border border-border">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-xs uppercase tracking-nav transition-colors hover:bg-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-fg [&::-webkit-details-marker]:hidden">
                <span>Advanced settings</span>
                <ChevronDown size={16} strokeWidth={1.5} aria-hidden="true" className="shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <div className="grid grid-cols-1 items-end gap-x-6 gap-y-5 border-t border-border p-4 sm:grid-cols-2">
                <div className="min-w-0 space-y-2">
                  <label htmlFor="task-signal-grouping" className={ADVANCED_LABEL_CLASS}>
                    Signal grouping
                    <FieldHelp>Per transaction nets the tokens each transaction moves, so an arbitrage that buys and sells the same tokens across this token's pools triggers nothing, while what a transaction keeps bought or sold is answered at that size. Per block also adds up all trades of a block into one counter-trade and leaves out traders who both buy and sell in that block (sandwich bots); they are then ignored for an hour.</FieldHelp>
                  </label>
                  <select id="task-signal-grouping" className="input h-10" value={aggregation} onChange={(e) => setAggregation(e.target.value as "" | "per-tx" | "per-block")}>
                    <option value="">{chain === "robinhood" ? "Default: per transaction" : "Default: per block"}</option>
                    <option value="per-tx">Per transaction</option>
                    <option value="per-block">Per block</option>
                  </select>
                </div>
                <div className="min-w-0 space-y-2">
                  <label htmlFor="task-min-leg-usd" className={ADVANCED_LABEL_CLASS}>
                    Min leg USD
                    <FieldHelp>No wallet trade smaller than this is sent. A response below it is skipped with below-min-leg, and a remainder below it is left unfilled.</FieldHelp>
                    <span className="whitespace-nowrap text-muted normal-case tracking-normal">(optional)</span>
                  </label>
                  <input id="task-min-leg-usd" className="input h-10" type="number" min="0" value={minLegUsd} onChange={(e) => setMinLegUsd(e.target.value)} placeholder="empty = no minimum" />
                </div>
                <div className="min-w-0 space-y-2">
                  <label htmlFor="task-max-gas-pct" className={ADVANCED_LABEL_CLASS}>
                    Max gas, % of trade
                    <FieldHelp>Skip a counter-trade when its estimated gas at the current price exceeds this share of its size (gas-too-expensive).</FieldHelp>
                    <span className="whitespace-nowrap text-muted normal-case tracking-normal">(optional)</span>
                  </label>
                  <input id="task-max-gas-pct" className="input h-10" type="number" min="0" max="100" step="0.1" value={maxGasPct} onChange={(e) => setMaxGasPct(e.target.value)} placeholder={chain === "ethereum" ? "empty = 5% on Ethereum" : "empty = no limit"} />
                </div>
                <div className="min-w-0 space-y-2">
                  <label htmlFor="task-trader-cap-usd" className={ADVANCED_LABEL_CLASS}>
                    Per-trader cap, USD/hour
                    <FieldHelp>Counter-trades answering one trader address stop at this total within any hour (trader-cap). The cap applies once the trader's wallet is known from the transaction.</FieldHelp>
                    <span className="whitespace-nowrap text-muted normal-case tracking-normal">(optional)</span>
                  </label>
                  <input id="task-trader-cap-usd" className="input h-10" type="number" min="0" value={traderCapUsd} onChange={(e) => setTraderCapUsd(e.target.value)} placeholder="empty = no cap" />
                </div>
                <div className="min-w-0 space-y-2">
                  <label htmlFor="task-price-limit-usd" className={ADVANCED_LABEL_CLASS}>
                    Price limit USD
                    <FieldHelp>BUY trades only while the token price is at or below this value; SELL only while it is at or above it (price-limit).</FieldHelp>
                    <span className="whitespace-nowrap text-muted normal-case tracking-normal">(optional)</span>
                  </label>
                  <input id="task-price-limit-usd" className="input h-10" type="number" min="0" step="any" value={priceLimitUsd} onChange={(e) => setPriceLimitUsd(e.target.value)} placeholder="empty = any price" />
                </div>
                {chain === "ethereum" && (
                  <div className="min-w-0 space-y-2">
                    <label htmlFor="task-backrun" className={ADVANCED_LABEL_CLASS}>
                      Pending triggers
                      <FieldHelp>Backrun sends the counter-trade to block builders in a bundle right behind the pending trigger: it lands directly after it in the same block or not at all, and never reverts on-chain. A single-wallet response only; if the bundle misses, the confirmed trade is answered normally a block later.</FieldHelp>
                    </label>
                    <label className="flex h-10 items-center gap-2 text-sm cursor-pointer">
                      <input id="task-backrun" type="checkbox" checked={backrun} onChange={(e) => setBackrun(e.target.checked)} />
                      <span>Backrun with a bundle</span>
                    </label>
                  </div>
                )}
                {chain === "ethereum" && action === "sell" && (
                  <div className="min-w-0 space-y-2">
                    <label htmlFor="task-pre-approve" className={ADVANCED_LABEL_CLASS}>
                      Pre-approve sells
                      <FieldHelp>On start, each wallet holding the token approves the sell venue once (about 46k gas per wallet and venue), so its first sell does not wait an extra block for the approval.</FieldHelp>
                    </label>
                    <label className="flex h-10 items-center gap-2 text-sm cursor-pointer">
                      <input id="task-pre-approve" type="checkbox" checked={preApprove} onChange={(e) => setPreApprove(e.target.checked)} />
                      <span>Approve at task start</span>
                    </label>
                  </div>
                )}
              </div>
            </details>
          )}

          <div className="divider" />

          <div>
            <label className="label flex flex-wrap items-center gap-1.5">
              Ignore wallets (optional)
              <FieldHelp>Signals from these trader addresses are ignored. Excel import reads every sheet in .xls and .xlsx files. Public addresses are kept; private keys are converted to public addresses and are not stored in the task configuration.</FieldHelp>
              <span className="text-muted normal-case tracking-normal ml-2">
                Ignoring {ignoreCount}
              </span>
              <span className="text-muted normal-case tracking-normal ml-2">
                {isEvm
                  ? "0x-prefixed addresses, one per line — triggers from these are skipped"
                  : "base58 pubkeys, one per line — triggers from these wallets are skipped"}
              </span>
            </label>
            <div className="flex flex-wrap items-end gap-2 mb-2">
              <div className="min-w-56 flex-1">
                <label className="label flex items-center gap-1.5">
                  Excel password (optional)
                  <FieldHelp>The password is used only to open this upload. It is not saved in the task or database.</FieldHelp>
                </label>
                <input
                  className="input font-mono text-xs"
                  type="password"
                  value={ignoreImportPassword}
                  onChange={(event) => setIgnoreImportPassword(event.target.value)}
                  placeholder="Only for password-protected files"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={255}
                  disabled={ignoreImporting}
                />
              </div>
              <label className="btn btn-ghost px-2 py-1 text-[10px] cursor-pointer">
                <Upload size={12} strokeWidth={1.5} />
                {ignoreImporting ? "Importing…" : "Import Excel"}
                <input
                  type="file"
                  accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  disabled={ignoreImporting}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) void importIgnoreWallets(file);
                  }}
                />
              </label>
            </div>
            <textarea
              className="input font-mono text-xs h-20 resize-y"
              value={ignoreWalletsText}
              onChange={(e) => setIgnoreWalletsText(e.target.value)}
              placeholder="e.g. our own bot wallets, market makers, dev wallets"
            />
            {ignoreImportResult && (
              <div className="text-[11px] text-ok mt-1">{ignoreImportResult}</div>
            )}
          </div>

          <div>
            <label className="label flex items-center gap-1.5">
              {mode === "edit" ? "Wallets" : "Wallets (paste "}
              <FieldHelp>Private keys used to execute this task. Their public addresses are shown after saving. Never paste public addresses here.</FieldHelp>
              {mode === "create" &&
                (isEvm ? "0x-prefixed hex private keys" : "base58 private keys")}
              {mode === "create" && ", one per line)"}
            </label>
            {mode === "edit" && initial && initial.walletPubkeys.length > 0 && (
              <div className="mb-2 border border-border bg-hover px-3 py-2">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-[10px] uppercase tracking-nav text-muted">
                    Keeping {walletPubkeysToKeep.length} of {initial.walletPubkeys.length}
                    {newWalletKeys.length > 0 ? ` + ${newWalletKeys.length} new` : ""}
                  </div>
                  <button
                    type="button"
                    onClick={() => setWalletPubkeysToKeep([])}
                    disabled={walletPubkeysToKeep.length === 0}
                    className="btn btn-ghost px-2 py-1 text-err disabled:opacity-50"
                    title="Mark every saved wallet for removal; changes apply when saved"
                  >
                    <Trash2 size={12} />
                    <span>Clear all</span>
                  </button>
                </div>
                <ul className="font-mono text-[11px] space-y-0.5">
                  {initial.walletPubkeys.map((pk, i) => {
                    const kept = walletPubkeysToKeep.includes(pk);
                    return (
                      <li key={pk} className="flex items-center gap-2">
                        <span className={kept ? "" : "text-muted line-through"}>W{i + 1}</span>
                        <span className={kept ? "text-muted" : "text-muted line-through"}>
                          {pk.slice(0, 8)}…{pk.slice(-6)}
                        </span>
                        <button
                          type="button"
                          onClick={() => void navigator.clipboard?.writeText(pk).catch(() => {})}
                          className="text-[10px] text-muted hover:text-fg uppercase tracking-nav"
                          title="Copy full pubkey"
                        >
                          copy
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setWalletPubkeysToKeep((prev) =>
                              kept ? prev.filter((x) => x !== pk) : [...prev, pk],
                            )
                          }
                          className={`text-[10px] uppercase tracking-nav ${
                            kept ? "text-err hover:text-err" : "text-ok hover:text-ok"
                          }`}
                        >
                          {kept ? "remove" : "keep"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className="text-[10px] text-muted mt-2">
                  Changes apply on save. A task with no wallets stays stopped until wallets are added.
                </div>
              </div>
            )}
            {mode === "edit" && (
              <div className="text-[10px] uppercase tracking-nav text-muted mb-2">
                Add private keys
              </div>
            )}
            <WalletsInput chain={chain} mint={mint} value={walletKeys} onChange={setWalletKeys} />
          </div>

          {error && (
            <div className="text-xs text-err uppercase tracking-nav">{error}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onCancel} className="btn btn-ghost">
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={submitting}
            className="btn btn-primary disabled:opacity-50"
          >
            {submitting ? "Saving…" : mode === "create" ? "Save task" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
